import { useMemo, useState } from "react";
import { Filter, X } from "lucide-react";
import { SearchSelect } from "../components/SearchSelect";

export const docSiteNames = (doc) =>
  doc.multi_site && doc.sites?.length ? doc.sites.map((s) => s.site_name) : (doc.site_name ? [doc.site_name] : []);

const clientKey = (doc) => doc.client_id || `name:${doc.client_name || ""}`;

export function SiteNames({ doc }) {
  const names = docSiteNames(doc);
  if (!names.length) return <span className="text-slate-400 text-xs">—</span>;
  return (
    <div className="flex flex-wrap gap-1 max-w-[260px]">
      {names.map((n, i) => <span key={i} className="inline-block rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-[11px] font-semibold text-sky-800">{n}</span>)}
    </div>
  );
}

export function useClientSiteFilter(items) {
  const [client, setClient] = useState("");
  const [site, setSite] = useState("");
  const clients = useMemo(() => {
    const m = new Map();
    items.forEach((d) => { if (d.client_name) m.set(clientKey(d), d.client_name); });
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [items]);
  const sites = useMemo(() => {
    const set = new Set();
    items.filter((d) => !client || clientKey(d) === client).forEach((d) => docSiteNames(d).forEach((n) => n && set.add(n)));
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [items, client]);
  const match = (d) => (!client || clientKey(d) === client) && (!site || docSiteNames(d).includes(site));
  const changeClient = (c) => { setClient(c); setSite(""); };
  return { client, site, setClient: changeClient, setSite, clients, sites, match };
}

export function ClientSiteFilterBar({ f, prefix }) {
  const active = f.client || f.site;
  return (
    <div className="flex flex-wrap items-center gap-2 mb-4" data-testid={`${prefix}-filters`}>
      <Filter className="h-4 w-4 text-slate-400" />
      <SearchSelect data-testid={`${prefix}-filter-client`} value={f.client} onChange={(e) => f.setClient(e.target.value)}
        className="text-sm border border-slate-300 rounded-md px-3 py-1.5 bg-white min-w-[200px] focus:outline-none focus:ring-2 focus:ring-sky-500">
        <option value="">Tous les clients</option>
        {f.clients.map(([k, n]) => <option key={k} value={k}>{n}</option>)}
      </SearchSelect>
      <SearchSelect data-testid={`${prefix}-filter-site`} value={f.site} onChange={(e) => f.setSite(e.target.value)} disabled={!f.sites.length}
        className="text-sm border border-slate-300 rounded-md px-3 py-1.5 bg-white min-w-[200px] disabled:bg-slate-100 disabled:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500">
        <option value="">{f.sites.length ? "Tous les sites" : "Aucun site"}</option>
        {f.sites.map((n) => <option key={n} value={n}>{n}</option>)}
      </SearchSelect>
      {active && (
        <button data-testid={`${prefix}-filter-reset`} onClick={() => f.setClient("")} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-rose-600">
          <X className="h-3.5 w-3.5" /> Réinitialiser
        </button>
      )}
    </div>
  );
}
